import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';

function TenantModal({ tenant, onClose, onSaved }) {
  const isEdit = !!tenant;
  const [formData, setFormData] = useState({
    instituteName: tenant?.InstituteName || '',
    instituteCode: tenant?.InstituteCode || '',
    isActive: tenant?.IsActive !== undefined ? tenant.IsActive : true,
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.instituteName.trim() || !formData.instituteCode.trim()) {
      toast.error('Institute Name and Code are required');
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/tenants/${tenant.TenantID}`, formData);
        toast.success('Tenant updated successfully');
      } else {
        await api.post('/tenants', formData);
        toast.success('Tenant created successfully');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save tenant');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? 'Edit Tenant' : 'Add New Tenant'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Institute Name</label>
            <input
              type="text"
              placeholder="Enter institute name"
              value={formData.instituteName}
              onChange={(e) => handleChange('instituteName', e.target.value)}
              autoFocus
            />
          </div>
          <div className="form-group">
            <label>Institute Code</label>
            <input
              type="text"
              placeholder="Enter unique code (e.g., MIT, IITB)"
              value={formData.instituteCode}
              onChange={(e) => handleChange('instituteCode', e.target.value.toUpperCase())}
            />
          </div>
          <div className="form-group">
            <label>Status</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => handleChange('isActive', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
              <span style={{ fontSize: 14, color: formData.isActive ? '#2e7d32' : '#c62828' }}>
                {formData.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-outline" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : isEdit ? 'Update Tenant' : 'Create Tenant'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Tenants() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);

  const loadTenants = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/tenants');
      if (response.data.success) {
        setTenants(response.data.data);
      }
    } catch (err) {
      toast.error('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  const handleAdd = () => {
    setEditingTenant(null);
    setShowModal(true);
  };

  const handleEdit = (tenant) => {
    setEditingTenant(tenant);
    setShowModal(true);
  };

  const handleSaved = () => {
    setShowModal(false);
    setEditingTenant(null);
    loadTenants();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <>
      <div className="page-card">
        <div className="page-card-header">
          <h3>Tenants</h3>
          <button className="btn btn-primary" onClick={handleAdd}>
            + Add Tenant
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Loading tenants...</div>
        ) : tenants.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
            No tenants found. Click "Add Tenant" to create one.
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Institute Name</th>
                <th>Code</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((tenant, idx) => (
                <tr key={tenant.TenantID}>
                  <td>{idx + 1}</td>
                  <td style={{ fontWeight: 500 }}>{tenant.InstituteName}</td>
                  <td>
                    <code style={{ background: '#f0f0f0', padding: '2px 8px', borderRadius: 4, fontSize: 13 }}>
                      {tenant.InstituteCode}
                    </code>
                  </td>
                  <td>
                    <span className={`badge ${tenant.IsActive ? 'badge-active' : 'badge-inactive'}`}>
                      {tenant.IsActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{formatDate(tenant.CreatedAt)}</td>
                  <td>
                    <button className="btn btn-outline btn-sm" onClick={() => handleEdit(tenant)}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <TenantModal
          tenant={editingTenant}
          onClose={() => { setShowModal(false); setEditingTenant(null); }}
          onSaved={handleSaved}
        />
      )}
    </>
  );
}

export default Tenants;
